import { type ReactNode, useCallback } from 'react';
import type { TimesheetApprovalItem } from '@core/timesheetApproval/timesheetApproval.types';
import {
  AppliedFilters,
  createFieldFilterOptions,
  FilterDropdown,
  SearchBar,
} from '@/components/filters';
import type { TimesheetFilters as TimesheetFiltersState } from '@/modules/timesheets/utils/timesheetGroups.utils';

const keepStoredLabel = (value: string) => value;

interface TimesheetFiltersProps {
  items: TimesheetApprovalItem[];
  filters: TimesheetFiltersState;
  /** The Client filter is only useful across clients (tenant portal). */
  showClientFilter: boolean;
  showAccountManagerFilter: boolean;
  accountManagerByClientId: ReadonlyMap<string, string>;
  /** Shown at the start of the row, with the filters pushed to the right. */
  leading?: ReactNode;
  onChange: (filters: TimesheetFiltersState) => void;
}

const getStatusOptions =
  createFieldFilterOptions<TimesheetApprovalItem>('daySequence.status');
const getClientOptions =
  createFieldFilterOptions<TimesheetApprovalItem>('clientName');
const getSiteOptions =
  createFieldFilterOptions<TimesheetApprovalItem>('siteName');

const TimesheetFilters = ({
  items,
  filters,
  showClientFilter,
  showAccountManagerFilter,
  accountManagerByClientId,
  leading,
  onChange,
}: TimesheetFiltersProps) => {
  const update = (patch: Partial<TimesheetFiltersState>) =>
    onChange({ ...filters, ...patch });

  const getAccountManagerOptions = useCallback(
    (data: TimesheetApprovalItem[]) =>
      Array.from(
        new Set(
          data
            .map((item) => accountManagerByClientId.get(item.clientId))
            .filter((name): name is string => Boolean(name)),
        ),
      ).sort((left, right) => left.localeCompare(right)),
    [accountManagerByClientId],
  );

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        {leading && <div className="mr-auto">{leading}</div>}
        <SearchBar
          searchQuery={filters.search}
          setSearchQuery={(search) => update({ search })}
          placeholder="Search timesheets"
        />
        <FilterDropdown
          title="Status"
          data={items}
          getOptions={getStatusOptions}
          selectedFilters={filters.statuses}
          setSelectedFilters={(statuses) => update({ statuses })}
        />
        {showClientFilter && (
          <FilterDropdown
            title="Client"
            data={items}
            getOptions={getClientOptions}
            selectedFilters={filters.clientNames}
            setSelectedFilters={(clientNames) => update({ clientNames })}
          />
        )}
        {showAccountManagerFilter && (
          <FilterDropdown
            title="Account Manager"
            data={items}
            getOptions={getAccountManagerOptions}
            selectedFilters={filters.accountManagers}
            setSelectedFilters={(accountManagers) =>
              update({ accountManagers })
            }
            formatLabel={keepStoredLabel}
          />
        )}
        <FilterDropdown
          title="Site"
          data={items}
          getOptions={getSiteOptions}
          selectedFilters={filters.siteNames}
          setSelectedFilters={(siteNames) => update({ siteNames })}
        />
      </div>
      <AppliedFilters
        filterGroups={[
          {
            label: 'Status',
            values: filters.statuses,
            setValues: (statuses) => update({ statuses }),
          },
          {
            label: 'Client',
            values: filters.clientNames,
            setValues: (clientNames) => update({ clientNames }),
          },
          {
            label: 'Account Manager',
            values: filters.accountManagers,
            setValues: (accountManagers) => update({ accountManagers }),
            formatLabel: keepStoredLabel,
          },
          {
            label: 'Site',
            values: filters.siteNames,
            setValues: (siteNames) => update({ siteNames }),
          },
        ]}
      />
    </div>
  );
};

export default TimesheetFilters;
